import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { supabase } from '@/lib/supabase';
import {
  FileText,
  CheckCircle,
  Clock,
  Users,
  Shield,
  Workflow,
  LogIn,
  Mail,
  Lock
} from 'lucide-react';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [azureLoading, setAzureLoading] = useState(false);
  const [error, setError] = useState('');

  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        setError('E-mail ou senha inválidos');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleAzureLogin = async () => {
    try {
      setAzureLoading(true);
      setError('');

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email profile openid User.Read',
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        setError('Erro ao iniciar login com Microsoft');
        setAzureLoading(false);
      }
    } catch (err) {
      setError('Erro ao fazer login com Microsoft');
      setAzureLoading(false);
    }
  };

  const features = [
    {
      icon: FileText,
      title: 'Gestão Completa',
      description: 'Controle total sobre seus contratos em um único lugar',
    },
    {
      icon: Workflow,
      title: 'Workflows Inteligentes',
      description: 'Automação de processos de aprovação e notificações',
    },
    {
      icon: Clock,
      title: 'Acompanhamento',
      description: 'Alertas de vencimento e prazos importantes',
    },
    {
      icon: Users,
      title: 'Colaboração',
      description: 'Trabalhe em equipe com permissões granulares',
    },
    {
      icon: CheckCircle,
      title: 'Aprovações',
      description: 'Sistema robusto de aprovações multi-nível',
    },
    {
      icon: Shield,
      title: 'Segurança',
      description: 'Auditoria completa e controle de acesso',
    },
  ];

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Logo/Brand */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center shadow-lg">
              <FileText className="w-7 h-7 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">FiberX Contratos</h1>
              <p className="text-sm text-muted-foreground">Sistema de Gestão de Contratos</p>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-6 mb-12">
            <h2 className="text-4xl font-bold text-foreground leading-tight">
              Gerencie seus contratos
              <br />
              <span className="text-primary">de forma inteligente</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-md">
              Plataforma completa para gestão, aprovação e acompanhamento de contratos
              com workflows automatizados e controle total.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4 hover:bg-card/80 transition-colors"
              >
                <feature.icon className="w-6 h-6 text-primary mb-2" />
                <h3 className="font-semibold text-foreground text-sm mb-1">
                  {feature.title}
                </h3>
                <p className="text-xs text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-sm text-muted-foreground">
            Sistema interno • Acesso restrito
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">FiberX Contratos</h1>
            </div>
          </div>

          <div className="space-y-6">
            {/* Header */}
            <div className="space-y-2 text-center lg:text-left">
              <h2 className="text-3xl font-bold text-foreground">Bem-vindo de volta</h2>
              <p className="text-muted-foreground">
                Entre com suas credenciais para acessar o sistema
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="email">
                  E-mail
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="password">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive rounded-md p-3">
                  {error}
                </div>
              )}

              {/* Login Button */}
              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Ou continue com</span>
                </div>
              </div>

              {/* Microsoft Login */}
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full"
                onClick={handleAzureLogin}
                disabled={azureLoading || loading}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23" fill="none">
                  <path d="M0 0h11v11H0V0z" fill="#f25022" />
                  <path d="M12 0h11v11H12V0z" fill="#00a4ef" />
                  <path d="M0 12h11v11H0V12z" fill="#ffb900" />
                  <path d="M12 12h11v11H12V12z" fill="#7fba00" />
                </svg>
                {azureLoading ? 'Redirecionando...' : 'Login com Microsoft'}
              </Button>

              {/* First Access Info */}
              <div className="p-4 bg-accent/10 border border-accent/30 rounded-md">
                <p className="text-xs text-foreground">
                  <LogIn className="w-4 h-4 inline mr-1 text-accent" />
                  <strong>Primeiro acesso?</strong> Após fazer login com Microsoft, seu
                  acesso será enviado para aprovação de um administrador. Você receberá um
                  e-mail quando for aprovado.
                </p>
              </div>

              {/* Register Link */}
              <div className="text-center text-sm">
                <span className="text-muted-foreground">Não tem uma conta? </span>
                <Link to="/register" className="text-primary hover:underline font-medium">
                  Criar conta
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
